import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CRow,
  CBadge,
  CImage,
  CNav,
  CNavItem,
  CNavLink,
  CSpinner,
  CTabContent,
  CTabPane,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilCart, cilCheck, cilList, cilMinus, cilPlus, cilSend, cilTrash } from '@coreui/icons'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { buildApiUrl } from '../../utils/getConfiguration'
import { SplashScreen } from '@capacitor/splash-screen'
import EmptyStatePanel from '../../components/EmptyStatePanel'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'

const attachAbortListeners = (upstreamSignal, controller) => {
  if (!upstreamSignal) return () => {}

  if (upstreamSignal.aborted) {
    controller.abort()
    return () => {}
  }

  const forwardAbort = () => controller.abort()
  upstreamSignal.addEventListener('abort', forwardAbort)
  controller.signal.addEventListener(
    'abort',
    () => upstreamSignal.removeEventListener('abort', forwardAbort),
    { once: true },
  )

  return () => upstreamSignal.removeEventListener('abort', forwardAbort)
}

const formatOrderDate = () => {
  const today = new Date()
  const day = String(today.getDate()).padStart(2, '0')
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const year = today.getFullYear()
  return `${day}.${month}.${year}`
}

const Eshop = () => {
  const navigate = useNavigate()
  const [accountId] = useState(() => localStorage.getItem('account_ID') || '')
  const [membership] = useState(() => localStorage.getItem('membership'))
  const [email] = useState(() => localStorage.getItem('mail') || '')

  const [accounts, setAccounts] = useState([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [accountsError, setAccountsError] = useState(null)

  const [items, setItems] = useState([])
  const itemsRef = useRef(items)
  const [itemsLoading, setItemsLoading] = useState(false)
  const [itemsError, setItemsError] = useState(null)
  const itemsFetchInFlight = useRef(null)

  const [catalogueItems, setCatalogueItems] = useState([])
  const catalogueRef = useRef(catalogueItems)
  const [catalogueLoading, setCatalogueLoading] = useState(false)
  const [catalogueError, setCatalogueError] = useState(null)
  const catalogueFetchInFlight = useRef(null)

  const [actionFeedback, setActionFeedback] = useState(null)
  const [recentlyAddedItemId, setRecentlyAddedItemId] = useState(null)
  const [activeTab, setActiveTab] = useState('catalogue')
  const { t } = useTranslation()

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    catalogueRef.current = catalogueItems
  }, [catalogueItems])

  useEffect(() => {
    if (!actionFeedback) return
    const timeout = setTimeout(() => setActionFeedback(null), 3500)
    return () => clearTimeout(timeout)
  }, [actionFeedback])

  useEffect(() => {
    if (!recentlyAddedItemId) return
    const timeout = setTimeout(() => setRecentlyAddedItemId(null), 3000)
    return () => clearTimeout(timeout)
  }, [recentlyAddedItemId])

  const hideSplashScreen = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    const splash = document.getElementById('splash-screen')
    if (splash) {
      splash.style.display = 'none'
    }

    if (typeof SplashScreen?.hide === 'function') {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
        console.warn('Unable to hide splash screen on Eshop load', error)
      })
    } else if (typeof window.SplashScreen?.hide === 'function') {
      window.SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
        console.warn('Unable to hide splash screen on Eshop load', error)
      })
    }
  }, [])

  const isCanceledError = useCallback(
    (error) =>
      apiClient.isCancel?.(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError',
    [],
  )

  const fetchAccountsData = useCallback(
    async ({ signal } = {}) => {
      if (!email) {
        setAccounts([])
        return
      }

      setAccountsLoading(true)
      setAccountsError(null)
      try {
        const response = await apiClient.get(buildApiUrl('account'), {
          params: { email },
          signal,
        })
        setAccounts(Array.isArray(response.data) ? response.data : [])
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error fetching account information', error)
        setAccountsError(t('eshop.errors.accounts'))
      } finally {
        setAccountsLoading(false)
      }
    },
    [email, isCanceledError, t],
  )

  const fetchItemsData = useCallback(
    async ({ signal, skipLoading = false } = {}) => {
      if (!accountId) {
        itemsRef.current = []
        setItems([])
        return
      }

      if (itemsFetchInFlight.current) {
        itemsFetchInFlight.current.abort()
      }

      const controller = new AbortController()
      const detachAbort = attachAbortListeners(signal, controller)
      itemsFetchInFlight.current = controller

      if (!skipLoading) {
        setItemsLoading(true)
      }
      setItemsError(null)

      try {
        const response = await apiClient.get(buildApiUrl('eshop'), {
          params: { account_ID: accountId },
          signal: controller.signal,
        })
        const nextItems = Array.isArray(response.data) ? response.data : []
        itemsRef.current = nextItems
        setItems(nextItems)
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error fetching eshop cart items', error)
        setItemsError(t('eshop.errors.items'))
      } finally {
        if (!skipLoading) {
          setItemsLoading(false)
        }
        detachAbort()
        if (itemsFetchInFlight.current === controller) {
          itemsFetchInFlight.current = null
        }
      }
    },
    [accountId, isCanceledError, t],
  )

  const fetchCatalogueData = useCallback(
    async ({ signal, skipLoading = false } = {}) => {
      const reminderId = localStorage.getItem('taskid')
      if (!reminderId) {
        catalogueRef.current = []
        setCatalogueItems([])
        return
      }

      if (catalogueFetchInFlight.current) {
        catalogueFetchInFlight.current.abort()
      }

      const controller = new AbortController()
      const detachAbort = attachAbortListeners(signal, controller)
      catalogueFetchInFlight.current = controller

      if (!skipLoading) {
        setCatalogueLoading(true)
      }
      setCatalogueError(null)

      try {
        const response = await apiClient.get(buildApiUrl('eshopCatalogue'), {
          params: { reminder_ID: reminderId },
          signal: controller.signal,
        })
        const nextCatalogue = Array.isArray(response.data) ? response.data : []
        catalogueRef.current = nextCatalogue
        setCatalogueItems(nextCatalogue)
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error fetching eshop catalogue items', error)
        setCatalogueError(t('eshop.errors.catalogue'))
      } finally {
        if (!skipLoading) {
          setCatalogueLoading(false)
        }
        detachAbort()
        if (catalogueFetchInFlight.current === controller) {
          catalogueFetchInFlight.current = null
        }
      }
    },
    [isCanceledError, t],
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchAccountsData({ signal: controller.signal })
    hideSplashScreen()
    return () => controller.abort()
  }, [fetchAccountsData, hideSplashScreen])

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      await fetchItemsData({ signal: controller.signal })
      hideSplashScreen()
    })()
    return () => {
      controller.abort()
      if (itemsFetchInFlight.current) {
        itemsFetchInFlight.current.abort()
        itemsFetchInFlight.current = null
      }
    }
  }, [fetchItemsData, hideSplashScreen])

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      await fetchCatalogueData({ signal: controller.signal })
      hideSplashScreen()
    })()
    return () => {
      controller.abort()
      if (catalogueFetchInFlight.current) {
        catalogueFetchInFlight.current.abort()
        catalogueFetchInFlight.current = null
      }
    }
  }, [fetchCatalogueData, hideSplashScreen])

  const getComparableId = useCallback((item) => String(item?.cart_item_id ?? item?.item_id), [])

  const handleItemDelete = useCallback(
    async (cartItemId) => {
      if (!accountId || !cartItemId) return

      setActionFeedback(null)
      const previousItems = itemsRef.current
      setItems((prev) => {
        const filtered = prev.filter(
          (existingItem) => getComparableId(existingItem) !== String(cartItemId),
        )
        itemsRef.current = filtered
        return filtered
      })

      try {
        await apiClient.get(buildApiUrl('eshopItemDelete'), {
          params: { account_ID: accountId, cart_item_ID: cartItemId },
        })
        setActionFeedback({ type: 'success', message: t('eshop.feedback.itemRemoved') })
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error removing item from buying list', error)
        itemsRef.current = previousItems
        setItems(previousItems)
        setActionFeedback({ type: 'error', message: t('eshop.feedback.itemRemoveError') })
      }
    },
    [accountId, getComparableId, isCanceledError, t],
  )

  const handleItemAmountChange = useCallback(
    async (cartItemId, newAmount) => {
      const parsedAmount = Number(newAmount)
      if (!cartItemId || !Number.isFinite(parsedAmount) || parsedAmount < 1) return

      setActionFeedback(null)
      const previousItems = itemsRef.current
      setItems((prev) => {
        const updated = prev.map((existingItem) =>
          getComparableId(existingItem) === String(cartItemId)
            ? { ...existingItem, amount: parsedAmount }
            : existingItem,
        )
        itemsRef.current = updated
        return updated
      })

      try {
        await apiClient.get(buildApiUrl('eshopItemCountUpdate'), {
          params: { amount: parsedAmount, item_id: cartItemId },
        })
        setActionFeedback({ type: 'success', message: t('eshop.feedback.quantityUpdated') })
        fetchItemsData({ skipLoading: true }).catch((fetchError) => {
          if (!isCanceledError(fetchError)) {
            console.error('Error refreshing buying list after quantity update', fetchError)
          }
        })
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error updating item amount', error)
        itemsRef.current = previousItems
        setItems(previousItems)
        setActionFeedback({
          type: 'error',
          message: t('eshop.feedback.quantityUpdateError'),
        })
      }
    },
    [fetchItemsData, getComparableId, isCanceledError, t],
  )

  const handleItemStatusChange = useCallback(
    async (cartItemId) => {
      if (!cartItemId) return

      setActionFeedback(null)
      const previousItems = itemsRef.current
      setItems((prev) => {
        const filtered = prev.filter(
          (existingItem) => getComparableId(existingItem) !== String(cartItemId),
        )
        itemsRef.current = filtered
        return filtered
      })

      try {
        await apiClient.get(buildApiUrl('eshopItemStatus'), {
          params: { status: '"B"', item_id: cartItemId },
        })
        setActionFeedback({ type: 'success', message: t('eshop.feedback.itemBought') })
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error updating item status', error)
        itemsRef.current = previousItems
        setItems(previousItems)
        setActionFeedback({
          type: 'error',
          message: t('eshop.feedback.itemBoughtError'),
        })
      }
    },
    [getComparableId, isCanceledError, t],
  )

  const handleCatalogueAdd = useCallback(
    async (catalogueItem) => {
      const catalogueId = catalogueItem?.item_id ?? catalogueItem?.catalogue_ID
      if (!accountId || !catalogueId) return

      setActionFeedback(null)
      setRecentlyAddedItemId(null)

      try {
        await apiClient.get(buildApiUrl('eshopItemAdd'), {
          params: {
            account_ID: accountId,
            catalogue_ID: catalogueId,
            orderdate: formatOrderDate(),
          },
        })
        setRecentlyAddedItemId(String(catalogueId))
        fetchItemsData({ skipLoading: true }).catch((fetchError) => {
          if (!isCanceledError(fetchError)) {
            console.error('Error refreshing buying list after add', fetchError)
          }
        })
      } catch (error) {
        if (isCanceledError(error)) return
        console.error('Error adding catalogue item', error)
        setActionFeedback({ type: 'error', message: t('eshop.feedback.addItemError') })
      }
    },
    [accountId, fetchItemsData, isCanceledError, t],
  )

  const primaryAccount = accounts[0]
  const accountLabel =
    primaryAccount?.name || primaryAccount?.email || primaryAccount?.login || email || null

  const renderCartTable = () => (
    <CTable align="middle" className="mb-0 buying-list-table eshop-cart-table" hover responsive>
      <CTableHead color="light">
        <CTableRow>
          <CTableHeaderCell className="text-center text-md-start"></CTableHeaderCell>
          <CTableHeaderCell className="text-center">{t('eshop.cart.headers.pcs')}</CTableHeaderCell>
          <CTableHeaderCell className="text-center">
            {t('eshop.cart.headers.action')}
          </CTableHeaderCell>
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {itemsLoading ? (
          <CTableRow>
            <CTableDataCell colSpan={3} className="text-center">
              <CSpinner size="sm" className="me-2" /> {t('eshop.cart.loading')}
            </CTableDataCell>
          </CTableRow>
        ) : itemsError ? (
          <CTableRow>
            <CTableDataCell colSpan={3}>
              <CAlert color="danger" className="mb-0">
                {itemsError}
              </CAlert>
            </CTableDataCell>
          </CTableRow>
        ) : items.length > 0 ? (
          items.map((item) => {
            const cartItemId = item?.cart_item_id ?? item?.item_id
            if (cartItemId == null) {
              console.warn('Skipping buying list item without identifier', item)
              return null
            }

            const amount = Number(item?.amount) || 0
            const membershipMatches = String(item?.membership_name) === String(membership)
            const rowKey = String(cartItemId)
            const itemCellClass = `buying-list-item-cell${membershipMatches ? '' : ' is-disabled'}`

            return (
              <CTableRow key={rowKey} className="buying-list-row">
                <CTableDataCell
                  color={membershipMatches ? 'secondary' : 'white'}
                  className={itemCellClass}
                >
                  <div className="d-flex align-items-center gap-3 buying-list-item">
                    {item?.pic ? (
                      <CImage
                        rounded
                        orientation="top"
                        src={item.pic}
                        width={64}
                        height={64}
                        className="buying-list-thumb"
                      />
                    ) : (
                      <div className="buying-list-thumb placeholder">
                        {t('eshop.common.noImage')}
                      </div>
                    )}
                    <div className="text-start">
                      <div className="fw-semibold">{item?.name}</div>
                      <div className="small text-medium-emphasis">{item?.category}</div>
                      {/*
                      {membershipMatches || !item?.membership_name ? null : (
                        <div className="small text-warning mt-1">{item?.membership_name}</div>
                      )}
                      */}
                    </div>
                  </div>
                </CTableDataCell>
                <CTableDataCell
                  data-label={t('eshop.cart.headers.pcs')}
                  className="text-center"
                  color="secondary"
                >
                  <div className="d-grid gap-2 d-md-flex justify-content-md-center align-items-center">
                    <CButton
                      color="primary"
                      disabled={amount <= 1}
                      onClick={() => handleItemAmountChange(cartItemId, amount - 1)}
                    >
                      <CIcon icon={cilMinus} className="me-2" />
                    </CButton>
                    <div className="text-center fw-semibold">{amount}</div>
                    <CButton
                      color="primary"
                      onClick={() => handleItemAmountChange(cartItemId, amount + 1)}
                    >
                      <CIcon icon={cilPlus} className="me-2" />
                    </CButton>
                  </div>
                </CTableDataCell>
                <CTableDataCell
                  data-label={t('eshop.cart.headers.action')}
                  className="text-center"
                  color="secondary"
                >
                  <div className="d-flex justify-content-center gap-2">
                    <CButton
                      color="primary"
                      variant="outline"
                      onClick={() => handleItemDelete(cartItemId)}
                      aria-label={t('eshop.actions.removeItemAria')}
                    >
                      <CIcon icon={cilTrash} className="me-2" />
                    </CButton>
                    <CButton
                      color="primary"
                      onClick={() => handleItemStatusChange(cartItemId)}
                      aria-label="Mark as bought"
                    >
                      <CIcon icon={cilCheck} className="me-2" />
                    </CButton>
                  </div>
                </CTableDataCell>
              </CTableRow>
            )
          })
        ) : (
          <CTableRow>
            <CTableDataCell colSpan={3}>
              <EmptyStatePanel
                icon={cilCart}
                title={t('eshop.cart.emptyTitle')}
                message={t('eshop.cart.emptyMessage')}
              />
            </CTableDataCell>
          </CTableRow>
        )}
      </CTableBody>
    </CTable>
  )

  const renderCatalogueTable = () => (
    <CTable
      align="middle"
      className="mb-0 buying-list-table eshop-catalogue-table"
      hover
      responsive
    >
      <CTableHead color="light">
        <CTableRow>
          <CTableHeaderCell className="text-center text-md-start"></CTableHeaderCell>
          <CTableHeaderCell className="text-center">
            {t('eshop.catalogue.headers.area')}
          </CTableHeaderCell>
          <CTableHeaderCell className="text-center">
            {t('eshop.catalogue.headers.action')}
          </CTableHeaderCell>
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {catalogueLoading ? (
          <CTableRow>
            <CTableDataCell colSpan={3} className="text-center">
              <CSpinner size="sm" className="me-2" /> {t('eshop.catalogue.loading')}
            </CTableDataCell>
          </CTableRow>
        ) : catalogueError ? (
          <CTableRow>
            <CTableDataCell colSpan={3}>
              <CAlert color="danger" className="mb-0">
                {catalogueError}
              </CAlert>
            </CTableDataCell>
          </CTableRow>
        ) : catalogueItems.length > 0 ? (
          catalogueItems.map((item) => {
            const catalogueId = item?.item_id ?? item?.catalogue_ID
            if (catalogueId == null) {
              console.warn('Skipping catalogue item without identifier', item)
              return null
            }

            const membershipMatches = String(item?.membership_name) === String(membership)
            const rowKey = String(catalogueId)
            const itemCellClass = `buying-list-item-cell${membershipMatches ? '' : ' is-disabled'}`

            return (
              <CTableRow key={rowKey} className="buying-list-row">
                <CTableDataCell
                  color={membershipMatches ? 'secondary' : 'white'}
                  className={itemCellClass}
                >
                  <div className="d-flex align-items-center gap-3 buying-list-item">
                    {item?.pic ? (
                      <CImage
                        rounded
                        orientation="top"
                        src={item.pic}
                        width={64}
                        height={64}
                        className="buying-list-thumb"
                      />
                    ) : (
                      <div className="buying-list-thumb placeholder">
                        {t('eshop.common.noImage')}
                      </div>
                    )}
                    <div className="text-start">
                      <div className="fw-semibold">{item?.name}</div>
                      {/*
                      <div className="small text-medium-emphasis">{item?.description}</div>
                      {membershipMatches || !item?.membership_name ? null : (
                        <div className="small text-warning mt-1">{item?.membership_name}</div>
                      )}
                      */}
                    </div>
                  </div>
                </CTableDataCell>
                <CTableDataCell
                  data-label={t('eshop.catalogue.headers.area')}
                  className="text-center"
                  color={membershipMatches ? 'secondary' : 'white'}
                >
                  <div className="small text-medium-emphasis">{item?.category}</div>
                </CTableDataCell>
                <CTableDataCell
                  data-label={t('eshop.catalogue.headers.action')}
                  className="text-center"
                  color="secondary"
                >
                  <CButton
                    color="primary"
                    variant="outline"
                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: '3.5rem', height: '3.5rem' }}
                    onClick={() => handleCatalogueAdd(item)}
                    aria-label={t('eshop.actions.addToCartAria')}
                  >
                    <CIcon icon={cilPlus} size="lg" className="me-1" />
                    <CIcon icon={cilCart} size="xl" />
                  </CButton>
                  {recentlyAddedItemId === rowKey && (
                    <div className="text-success mt-2" aria-label={t('eshop.common.itemAdded')}>
                      <CIcon icon={cilCheck} size="lg" />
                    </div>
                  )}
                </CTableDataCell>
              </CTableRow>
            )
          })
        ) : (
          <CTableRow>
            <CTableDataCell colSpan={3}>
              <EmptyStatePanel
                icon={cilList}
                title={t('eshop.catalogue.emptyTitle')}
                message={t('eshop.catalogue.emptyMessage')}
              />
            </CTableDataCell>
          </CTableRow>
        )}
      </CTableBody>
    </CTable>
  )

  return (
    <div className="eshop-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader title={t('eshop.header.title')} onBack={() => navigate(-1)} />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <CCard className="border-0 shadow-sm">
              <CCardBody className="p-4 p-lg-5">
                {actionFeedback && (
                  <CAlert
                    color={actionFeedback.type === 'error' ? 'danger' : 'success'}
                    className="mb-4"
                    dismissible
                    onClose={() => setActionFeedback(null)}
                  >
                    {actionFeedback.message}
                  </CAlert>
                )}

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                  <CNav variant="tabs" role="tablist">
                    <CNavItem>
                      <CNavLink
                        role="tab"
                        active={activeTab === 'cart'}
                        onClick={() => setActiveTab('cart')}
                      >
                        {t('eshop.nav.cart')}{' '}
                        <CBadge color={items.length === 0 ? 'warning' : 'primary'} className="ms-2">
                          {items.length}
                        </CBadge>
                      </CNavLink>
                    </CNavItem>
                    <CNavItem>
                      <CNavLink
                        role="tab"
                        active={activeTab === 'catalogue'}
                        onClick={() => setActiveTab('catalogue')}
                      >
                        {t('eshop.nav.catalogue')}
                      </CNavLink>
                    </CNavItem>
                  </CNav>
                  {activeTab === 'cart' && (
                    <div className="buying-list-actions mb-0">
                      <CButton
                        color="primary"
                        className="buying-list-actions__button"
                        onClick={() => navigate('/eshopbought')}
                        aria-label={t('eshop.actions.showBoughtAria')}
                      >
                        <CIcon icon={cilList} size="xl" />
                      </CButton>
                      <CButton
                        color="primary"
                        className="buying-list-actions__button buying-list-actions__button--secondary"
                        onClick={() => navigate('/dashboard')}
                        aria-label={t('eshop.actions.goDashboardAria')}
                      >
                        <CIcon icon={cilSend} size="xl" />
                      </CButton>
                    </div>
                  )}
                </div>

                <CTabContent>
                  <CTabPane role="tabpanel" visible={activeTab === 'cart'}>
                    {renderCartTable()}
                  </CTabPane>
                  <CTabPane role="tabpanel" visible={activeTab === 'catalogue'}>
                    {renderCatalogueTable()}
                  </CTabPane>
                </CTabContent>

                <div className="small text-medium-emphasis mt-4">
                  {accountLabel && (
                    <span>{t('eshop.accountInfo.label', { account: accountLabel })}</span>
                  )}
                  {accountsLoading && (
                    <span className={accountLabel ? 'ms-2' : undefined}>
                      {t('eshop.accountInfo.loading')}
                    </span>
                  )}
                  {accountsError && <span className="text-danger ms-2">{accountsError}</span>}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Eshop
